import { useCallback, useRef, useState } from "react";
import { DIFFICULTIES, DIFFICULTY_LABELS, DIFFICULTY_COLORS, DIFFICULTY_EMOJI } from "../types";
import type { Difficulty } from "../types";
import { useOutsideClick } from "../hooks/useOutsideClick";
import styles from "./DifficultyBadge.module.css";

interface DifficultyBadgeProps {
  difficulty: Difficulty;
  isRandom: boolean;
  onSelectDifficulty?: (difficulty: Difficulty) => void;
}

export function DifficultyBadge({ difficulty, isRandom, onSelectDifficulty }: DifficultyBadgeProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const closeBadge = useCallback(() => setOpen(false), []);
  useOutsideClick(wrapRef, open, closeBadge);

  const color = DIFFICULTY_COLORS[difficulty];
  const emoji = DIFFICULTY_EMOJI[difficulty];
  const label = DIFFICULTY_LABELS[difficulty];

  if (!isRandom) {
    return (
      <span
        className={`${styles.badge} ${styles.daily}`}
        style={{ borderColor: color, color }}
        title={label}
      >
        <span className={styles.emoji}>{emoji}</span>
        <span className={styles.label}>{label}</span>
      </span>
    );
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        className={styles.badge}
        style={{ borderColor: color, color }}
        onClick={() => setOpen((v) => !v)}
        aria-label="Change difficulty"
      >
        <span className={styles.emoji}>{emoji}</span>
        <span className={styles.chevron} aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          {DIFFICULTIES.map((d) => (
            <button
              key={d}
              className={`${styles.dropdownItem} ${d === difficulty ? styles.dropdownItemActive : ""}`}
              onClick={() => {
                setOpen(false);
                onSelectDifficulty?.(d);
              }}
            >
              <span className={styles.dropdownEmoji}>{DIFFICULTY_EMOJI[d]}</span>
              <span style={{ color: DIFFICULTY_COLORS[d] }}>{DIFFICULTY_LABELS[d]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
